import React from 'react';
import { Info, BarChart2, Layers, Lightbulb, RefreshCw, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о 3D-печати и аддитивном производстве
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-teal-500" />
        О 3D-печати и аддитивном производстве
      </h2>
      
      <div className="prose prose-teal dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-teal-500" />
          Что такое аддитивное производство?
        </h3>
        <p>
          <strong>Аддитивное производство</strong> (или 3D-печать) — это процесс создания трехмерных объектов путем последовательного нанесения слоев материала по цифровой 3D-модели. В отличие от традиционных методов производства, где детали создаются путем удаления материала (фрезеровка, точение) или формования, аддитивное производство добавляет материал только там, где он необходим.
        </p>

        <h3 className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-teal-500" />
          Основные технологии 3D-печати
        </h3>
        <ul>
          <li>
            <strong>FDM (Fused Deposition Modeling)</strong> — послойное наплавление термопластика. Самая распространенная и доступная технология. Материалы: PLA, ABS, PETG, TPU, нейлон.
          </li>
          <li>
            <strong>SLA (Stereolithography)</strong> — стереолитография. Точная печать с использованием жидкой фотополимерной смолы, затвердевающей под воздействием УФ-лазера. Обеспечивает высокое качество поверхности.
          </li>
          <li>
            <strong>SLS (Selective Laser Sintering)</strong> — селективное лазерное спекание. Использует порошковые материалы (обычно полиамиды, нейлон), которые спекаются лазером. Не требует поддержек, высокая прочность.
          </li>
          <li>
            <strong>SLM/DMLS (Selective Laser Melting/Direct Metal Laser Sintering)</strong> — селективное лазерное плавление металлических порошков. Используется для создания металлических деталей высокой прочности.
          </li>
          <li>
            <strong>PolyJet</strong> — струйная печать фотополимерами. Позволяет создавать многоцветные изделия и комбинировать разные материалы в одной модели.
          </li>
        </ul>

        <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-100 dark:border-teal-800 my-6">
          <h4 className="text-teal-700 dark:text-teal-300 font-medium flex items-center gap-2">
            <Lightbulb className="w-4 h-4" />
            Выбор технологии печати
          </h4>
          <p className="text-teal-600 dark:text-teal-400 text-sm mb-0">
            При выборе технологии печати ориентируйтесь на требования к изделию: для функциональных прототипов подойдет FDM, для высокодетализированных моделей — SLA, для прочных деталей — SLS, для металлических изделий — SLM/DMLS.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-teal-500" />
          Процесс 3D-печати
        </h3>
        <ol>
          <li><strong>Создание 3D-модели</strong> в CAD-программе или получение готовой модели.</li>
          <li><strong>Экспорт модели в STL-формат</strong> (или другой поддерживаемый формат).</li>
          <li><strong>Слайсинг</strong> — разбиение модели на слои и создание G-кода с помощью специального ПО (Cura, PrusaSlicer, Chitubox и др.).</li>
          <li><strong>Подготовка принтера</strong> — калибровка, загрузка материала, предварительный нагрев.</li>
          <li><strong>Печать</strong> — послойное создание физического объекта по цифровой модели.</li>
          <li><strong>Постобработка</strong> — удаление поддержек, шлифовка, полировка, окрашивание и другие отделочные операции.</li>
        </ol>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-teal-500" />
          Преимущества аддитивного производства
        </h3>
        <ul>
          <li><strong>Свобода дизайна</strong> — возможность создания деталей сложной геометрии, которые невозможно или очень дорого изготовить традиционными методами.</li>
          <li><strong>Быстрое прототипирование</strong> — сокращение времени от идеи до готового прототипа с недель до дней или часов.</li>
          <li><strong>Кастомизация</strong> — возможность легко изменять дизайн для создания персонализированных изделий без дополнительных затрат.</li>
          <li><strong>Минимальные отходы</strong> — используется только необходимое количество материала.</li>
          <li><strong>Сокращение логистических затрат</strong> — возможность производства деталей на месте использования.</li>
          <li><strong>Уменьшение количества сборочных операций</strong> — возможность печати сложных деталей как единого целого.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Ограничения 3D-печати
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Ограниченная скорость производства (особенно для крупных партий)</li>
            <li>Ограниченная точность и качество поверхности (зависит от технологии)</li>
            <li>Анизотропия свойств (разная прочность в разных направлениях)</li>
            <li>Ограниченный выбор материалов по сравнению с традиционными методами</li>
            <li>Высокая стоимость некоторых технологий и материалов</li>
          </ul>
        </div>

        <h3>Области применения 3D-печати</h3>
        <ul>
          <li><strong>Прототипирование</strong> — быстрое тестирование идей и концепций перед производством.</li>
          <li><strong>Производство мелких серий</strong> — экономически выгодное изготовление небольших партий продукции.</li>
          <li><strong>Медицина</strong> — индивидуальные протезы, импланты, медицинские модели, биопечать.</li>
          <li><strong>Аэрокосмическая отрасль</strong> — легкие и прочные детали сложной геометрии.</li>
          <li><strong>Автомобилестроение</strong> — прототипы, запчасти, кастомизированные детали.</li>
          <li><strong>Архитектура</strong> — детализированные макеты зданий и комплексов.</li>
          <li><strong>Образование</strong> — наглядные учебные пособия и модели.</li>
          <li><strong>Искусство и дизайн</strong> — уникальные скульптуры, ювелирные изделия, предметы интерьера.</li>
          <li><strong>DIY и хобби</strong> — домашнее производство, мелкий ремонт, моделизм.</li>
        </ul>

        <h3>Как калькулировать стоимость 3D-печати</h3>
        <p>
          Правильный расчет стоимости 3D-печати важен как для частных лиц, так и для бизнеса. Основные компоненты затрат:
        </p>
        <ol>
          <li>
            <strong>Затраты на материал</strong> — стоимость филамента, смолы или порошка, включая расход на поддержки и неудачные печати.
          </li>
          <li>
            <strong>Затраты на электроэнергию</strong> — потребление принтера, постобработки, вспомогательного оборудования.
          </li>
          <li>
            <strong>Амортизация оборудования</strong> — распределение стоимости принтера и периферии на срок службы.
          </li>
          <li>
            <strong>Трудозатраты</strong> — время на подготовку, наблюдение, постобработку.
          </li>
          <li>
            <strong>Накладные расходы</strong> — аренда, обслуживание, программное обеспечение.
          </li>
          <li>
            <strong>Маржа прибыли</strong> — для коммерческих услуг 3D-печати.
          </li>
        </ol>
        <p>
          Наш калькулятор поможет вам учесть все эти компоненты и определить справедливую цену на услуги 3D-печати или себестоимость собственных изделий.
        </p>

        <h3>Тенденции развития аддитивного производства</h3>
        <ul>
          <li><strong>Повышение скорости печати</strong> и производительности оборудования.</li>
          <li><strong>Расширение спектра материалов</strong>, включая композитные, многоматериальную печать.</li>
          <li><strong>Улучшение механических свойств</strong> напечатанных деталей.</li>
          <li><strong>Интеграция с AI</strong> для оптимизации дизайна и параметров печати.</li>
          <li><strong>Развитие крупноформатной печати</strong> для строительства и крупногабаритных деталей.</li>
          <li><strong>Снижение стоимости оборудования</strong> и материалов, повышение доступности.</li>
          <li><strong>Экологичность</strong> — биоразлагаемые материалы, переработка отходов печати.</li>
        </ul>

        <p className="font-semibold mt-6">
          Аддитивное производство стремительно развивается и открывает новые возможности в различных отраслях. Используйте наш калькулятор для оценки затрат и принятия обоснованных решений о применении 3D-печати в ваших проектах!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;